import React, { useState, useEffect } from 'react'
import { Layout, Menu, theme, Avatar, Dropdown, Button, Grid, Drawer } from 'antd'
import {
  DashboardOutlined,
  PlayCircleOutlined,
  PlusCircleOutlined,
  AppstoreOutlined,
  TagOutlined,
  UserOutlined,
  ImportOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  QuestionCircleOutlined,
  FileTextOutlined,
  SettingOutlined,
  CloudServerOutlined,
  DollarOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'

const { Header, Sider, Content } = Layout

interface AdminLayoutProps {
  children: React.ReactNode
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false)
    }
  }, [isMobile])
  
  const {
    token: { colorBgContainer },
  } = theme.useToken()

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/games',
      icon: <PlayCircleOutlined />,
      label: 'Games',
    },
    {
      key: '/games/add',
      icon: <PlusCircleOutlined />,
      label: 'Add Games',
    },
    {
      key: '/categories',
      icon: <AppstoreOutlined />,
      label: 'Categories',
    },
    {
      key: '/tags',
      icon: <TagOutlined />,
      label: 'Tags',
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: 'Users',
    },
    {
      key: '/import-logs',
      icon: <FileTextOutlined />,
      label: 'Import Logs',
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      children: [
        {
          key: '/settings/storage',
          icon: <CloudServerOutlined />,
          label: 'Storage',
        },
        {
          key: '/settings/ads',
          icon: <DollarOutlined />,
          label: 'Ads & Monetization',
        },
      ],
    },
    {
      key: '/help',
      icon: <QuestionCircleOutlined />,
      label: 'Help',
    },
  ]

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key)
    if (isMobile) {
      setMobileMenuOpen(false)
    }
  }

  const handleLogout = () => {
    logout()
  }

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
    },
  ]

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      handleLogout()
    } else if (key === 'profile') {
      // Handle profile navigation if needed
      console.log('Profile clicked')
    }
  }

  const renderMenu = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[location.pathname]}
      items={menuItems}
      onClick={handleMenuClick}
      style={{ border: 'none' }}
    />
  )

  const brand = (
    <div
      style={{
        height: 32,
        margin: 16,
        background: 'rgba(255, 255, 255, 0.2)',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 'bold',
      }}
    >
      {collapsed && !isMobile ? 'GP' : 'Gaming Platform'}
    </div>
  )

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          collapsedWidth={80}
          width={220}
          breakpoint="lg"
        >
          {brand}
          {renderMenu}
        </Sider>
      )}

      {isMobile && (
        <Drawer
          placement="left"
          closable={false}
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          width={260}
          styles={{ 
            header: { display: 'none' },
            body: { padding: 0 }
          }}
        >
          <div
            style={{
              background: '#001529',
              height: '100%',
              color: 'white',
            }}
          >
            {brand}
            {renderMenu}
          </div>
        </Drawer>
      )}
      <Layout>
        <Header
          style={{
            padding: isMobile ? '0 12px' : 0,
            background: colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingRight: isMobile ? 12 : 24,
            borderBottom: '1px solid rgba(0,0,0,0.05)',
          }}
        >
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuUnfoldOutlined />}
              onClick={() => setMobileMenuOpen(true)}
              style={{
                fontSize: 20,
                width: 56,
                height: 56,
              }}
            />
          ) : (
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: 16,
                width: 64,
                height: 64,
              }}
            />
          )}

          <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }} placement="bottomRight">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: 6,
                transition: 'background-color 0.2s',
              }}
            >
              <Avatar size="small" icon={<UserOutlined />} />
              <span>{user?.username || 'Admin'}</span>
            </div>
          </Dropdown>
        </Header>
        <Content
          style={{
            margin: isMobile ? '16px 12px' : '24px 16px',
            padding: isMobile ? 16 : 24,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: 6,
          }}
        >
          {children as any}
        </Content>
      </Layout>
    </Layout>
  )
}

export default AdminLayout
